import {
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns';
import posthog from 'posthog-js';
import { signOut } from 'firebase/auth';
import { getDownloadURL, ref } from 'firebase/storage';
import { FilterOption, LoadedUser } from '../../interfaces';
import { storage, auth } from '../../firebase';

export function getFullName({
  firstName,
  lastName,
}: {
  firstName?: string | null;
  lastName?: string | null;
}): string {
  return `${firstName || ''} ${lastName || ''}`.trim();
}

// Turn label and action into an object for Modal Footer
export function createFooterAction(label: string, action: any) {
  const footerAction = {
    label,
    action,
  };

  return footerAction;
}

export function cx(...args: unknown[]) {
  return args
    .flat()
    .filter(x => typeof x === 'string')
    .join(' ')
    .trim();
}

export function getUserNameFromBusiness(
  loadedUser: LoadedUser | undefined,
  isCustomer: boolean,
  userId?: string | null,
) {
  if (isCustomer) return 'Customer';

  if (!userId) return '';

  const employee = loadedUser?.business.employees.find(
    e => e.employeeId === userId,
  );

  return employee
    ? getFullName({
        firstName: employee.user?.firstName,
        lastName: employee.user?.lastName,
      })
    : 'Unknown';
}

export function trimString(str: string, length: number) {
  return str.length > length ? `${str.slice(0, length)}...` : str;
}

/**
 * Check if the date is the same day or before the date to compare
 * @param date - The date to check
 * @param dateToCompare - The date to compare to
 * @returns True if the date is the same day or before the date to compare, false otherwise
 */
export function isSameDayOrBefore(date: Date, dateToCompare: Date) {
  return isSameDay(date, dateToCompare) || isBefore(date, dateToCompare);
}

/**
 * Check if the date is the same day or after the date to compare
 * @param date - The date to check
 * @param dateToCompare - The date to compare to
 * @returns True if the date is the same day or after the date to compare, false otherwise
 */
export function isSameDayOrAfter(date: Date, dateToCompare: Date) {
  return isSameDay(date, dateToCompare) || isAfter(date, dateToCompare);
}

export function isCuid(value: string) {
  return /^c[a-z0-9]{24}$/.test(value);
}

export async function getStorageUrl(url: string) {
  const urlObj = new URL(url);

  if (urlObj.searchParams.has('token') && urlObj.searchParams.get('alt')) {
    return url;
  }

  try {
    const urlNoParams = urlObj.toString().split('?')[0];
    const storageRef = ref(storage, urlNoParams);
    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
  } catch {
    return null;
  }
}

export async function openStorageUrl(url: string) {
  const storageUrl = await getStorageUrl(url);
  if (!storageUrl) return;
  window.open(storageUrl, '_blank');
}

export const getInitialFilterValue = (
  name: string,
  options: FilterOption[],
  type: 'MULTI' | 'SINGLE' | 'DATE_RANGE',
  defaultInitialValue?: string | boolean,
  isBoolean?: boolean,
) => {
  const defaultValue = type === 'MULTI' ? [] : undefined;

  const cachedValue = sessionStorage.getItem(name);

  let parsedCachedValue = cachedValue ? JSON.parse(cachedValue) : defaultValue;

  if (isBoolean && typeof parsedCachedValue === 'string') {
    parsedCachedValue = parsedCachedValue === 'true';
  }

  if (type === 'MULTI') {
    if (Array.isArray(parsedCachedValue) && parsedCachedValue.length > 0) {
      const filteredCachedList = parsedCachedValue.filter(value =>
        options.some(option => option.value === value),
      );
      if (filteredCachedList.length > 0) return filteredCachedList;
    }

    return options.map(option => option.value);
  }

  if (type === 'DATE_RANGE') {
    if (Array.isArray(parsedCachedValue) && parsedCachedValue.length === 2) {
      return [new Date(parsedCachedValue[0]), new Date(parsedCachedValue[1])];
    }
    return [startOfDay(subDays(new Date(), 7)), endOfDay(new Date())];
  }

  if (
    options.some(
      option =>
        option.value === parsedCachedValue ||
        (isBoolean && option.value === parsedCachedValue?.toString()),
    )
  ) {
    return parsedCachedValue;
  }

  return defaultInitialValue || defaultValue;
};

export async function handleSignOut() {
  posthog.reset();
  await signOut(auth);
}
